import {
  IdArticleOrderArticleMap,
  Order,
  OrderArticle,
  OrderResponseDto,
  ORDER_FILTERS,
  PickingList,
} from 'shared'
import { create } from 'zustand'

export interface OrderStoreState {
  orders: Order[]
  rawOrders: OrderResponseDto[]
  filteredOrders: Order[]
  view: string
  filterByOrder: number[]
  startDate: Date
  endDate: Date
  activeTab: number
  layout: string
  selectedArticle: OrderArticle | undefined
  activeOrdersFilters: string[]
  selectedOrderIds: number[]
  selectedOrders: Order[]
  activePickingList: PickingList | undefined
  articlesToPick: OrderArticle[]
  articlesPicked: OrderArticle[]
  articlesWithIssues: OrderArticle[]
  expandedOrder: Order | undefined
  lastPicked: OrderArticle | undefined
  articlesReadyForSorting: boolean
  expandedRowIndex: number | undefined
  loadingFromMkm: boolean
  loadingFromPt: boolean
  disablePickingButtons: boolean
  loadingPickingLists: boolean
  userPickingLists: PickingList[]
  mapOfArticlesToPick: IdArticleOrderArticleMap

  initializedOrders: boolean
  setInitializedOrders: (v: boolean) => void
  refetchArticlesToPick: ((sortingFields: string[]) => Promise<void>) | null

  setOrders: (newOrders: Order[]) => void
  setRawOrders: (newRawOrders: OrderResponseDto[]) => void
  setFilteredOrders: (newOrders: Order[]) => void
  setFilterByOrder: (newFilterByOrder: number[]) => void

  setView: (newView: string) => void
  setStartDate: (newStartDate: Date) => void
  setEndDate: (newEndDate: Date) => void
  setActiveTab: (activeTab: number) => void
  setLayout: (newLayout: string) => void
  setArticlesReadyForSorting: (newArticlesReadyForSorting: boolean) => void
  setExpandedRowIndex: (newExpandedRowIndex: number | undefined) => void
  setLoadingFromMkm: (newLoadingFromMkm: boolean) => void
  setLoadingFromPt: (newLoadingFromPt: boolean) => void
  setDisablePickingButtons: (newDisablePickingButtons: boolean) => void

  setSelectedArticle: (newSelectedArticle: OrderArticle | undefined) => void
  setActiveOrdersFilters: (newActiveOrdersFilters: string[]) => void
  setSelectedOrderIds: (newSelectedOrderIds: number[]) => void
  setSelectedOrders: (newSelectedOrders: Order[]) => void
  setActivePickingList: (newActivePickingList: PickingList | undefined) => void
  setArticlesToPick: (newArticlesToPick: OrderArticle[]) => void
  setArticlesPicked: (newArticlesPicked: OrderArticle[]) => void
  setArticlesWithIssues: (newArticlesWithIssues: OrderArticle[]) => void
  setExpandedOrder: (newExpandedOrder: Order | undefined) => void
  setLastPicked: (newLastPicked: OrderArticle | undefined) => void
  setUserPickingLists: (newUserPickingList: PickingList[]) => void
  setLoadingPickingLists: (v: boolean) => void
  setMapOfArticlesToPick: (newMapOfArticlesToPick: IdArticleOrderArticleMap) => void
  setRefetchArticlesToPick: (fn: ((sortingFields: string[]) => Promise<void>) | null) => void
}

const getDefaultStartDate = () => {
  const d = new Date()
  d.setMonth(d.getMonth() - 18)
  return d
}

export const useOrderStore = create<OrderStoreState>()((set) => ({
  orders: [],
  rawOrders: [],
  filteredOrders: [],
  view: 'list',
  filterByOrder: [],
  startDate: getDefaultStartDate(),
  endDate: new Date(),
  activeTab: -1,
  layout: 'text',
  selectedArticle: undefined,
  activeOrdersFilters: ORDER_FILTERS.filter((f) => f.defaultActive).map((f) => f.state),
  selectedOrderIds: [],
  selectedOrders: [],
  activePickingList: undefined,
  articlesToPick: [],
  articlesPicked: [],
  articlesWithIssues: [],
  expandedOrder: undefined,
  lastPicked: undefined,
  articlesReadyForSorting: false,
  expandedRowIndex: undefined,
  loadingFromMkm: false,
  loadingFromPt: false,
  disablePickingButtons: false,
  loadingPickingLists: false,
  userPickingLists: [],
  mapOfArticlesToPick: {},

  initializedOrders: false,
  setInitializedOrders: (v) => set({ initializedOrders: v }),
  refetchArticlesToPick: null,

  setOrders: (newOrders: Order[]) => set({ orders: newOrders }),
  setRawOrders: (newRawOrders: OrderResponseDto[]) => set({ rawOrders: newRawOrders }),
  setFilteredOrders: (newFilteredOrders: Order[]) => set({ filteredOrders: newFilteredOrders }),
  setFilterByOrder: (newFilterByOrder: number[]) => set({ filterByOrder: newFilterByOrder }),

  setView: (newView) => set((s) => (s.view === newView ? s : { view: newView })),
  setStartDate: (newStartDate) =>
    set((s) =>
      s.startDate.getTime() === newStartDate.getTime() ? s : { startDate: newStartDate }
    ),
  setEndDate: (newEndDate) =>
    set((s) => (s.endDate.getTime() === newEndDate.getTime() ? s : { endDate: newEndDate })),
  setActiveTab: (newActiveTab) =>
    set((s) => (s.activeTab === newActiveTab ? s : { activeTab: newActiveTab })),
  setLayout: (next) => set((s) => (s.layout === next ? s : { layout: next })),
  setArticlesReadyForSorting: (newReadyForSorting) =>
    set((s) =>
      s.articlesReadyForSorting === newReadyForSorting
        ? s
        : { articlesReadyForSorting: newReadyForSorting }
    ),
  setExpandedRowIndex: (newExpandedRow) =>
    set((s) => (s.expandedRowIndex === newExpandedRow ? s : { expandedRowIndex: newExpandedRow })),
  setLoadingFromMkm: (newLoadingFromMkm) =>
    set((s) =>
      s.loadingFromMkm === newLoadingFromMkm ? s : { loadingFromMkm: newLoadingFromMkm }
    ),
  setLoadingFromPt: (newLoadingFromPt) =>
    set((s) => (s.loadingFromPt === newLoadingFromPt ? s : { loadingFromPt: newLoadingFromPt })),
  setDisablePickingButtons: (newDisablePickingButtons) =>
    set((s) =>
      s.disablePickingButtons === newDisablePickingButtons
        ? s
        : { disablePickingButtons: newDisablePickingButtons }
    ),

  setSelectedArticle: (newSelectedArticle) => set({ selectedArticle: newSelectedArticle }),
  setActiveOrdersFilters: (newActiveOrdersFilters) =>
    set({ activeOrdersFilters: newActiveOrdersFilters }),
  setSelectedOrderIds: (newSelectedOrderIds) => set({ selectedOrderIds: newSelectedOrderIds }),
  setSelectedOrders: (newSelectedOrders) => set({ selectedOrders: newSelectedOrders }),
  setActivePickingList: (newActivePickingList) => set({ activePickingList: newActivePickingList }),
  setArticlesToPick: (newArticlesToPick) => set({ articlesToPick: newArticlesToPick }),
  setArticlesPicked: (newArticlesPicked) => set({ articlesPicked: newArticlesPicked }),
  setArticlesWithIssues: (newArticlesWithIssues) =>
    set({ articlesWithIssues: newArticlesWithIssues }),
  setExpandedOrder: (newExpandedOrder) => set({ expandedOrder: newExpandedOrder }),
  setLastPicked: (newLastPicked) => set({ lastPicked: newLastPicked }),
  setUserPickingLists: (newSetUserPickingList) => set({ userPickingLists: newSetUserPickingList }),
  setLoadingPickingLists: (v) => set({ loadingPickingLists: v }),
  setMapOfArticlesToPick: (newMapOfArticlesToPick) =>
    set({ mapOfArticlesToPick: newMapOfArticlesToPick }),

  setRefetchArticlesToPick: (fn) => set({ refetchArticlesToPick: fn }),
}))
